import React from "react";
import { FiMoreVertical } from "react-icons/fi";

import * as types from "types";
import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import FilePreviewModal, {
  findVariantPermalink,
  getFilePreviewTarget,
} from "components/Message/FilePreviewModal";
import ShareLinkModal from "components/Message/ShareLinkModal";
import useDownloadFiles from "../hooks/useDownloadFiles";
import splitFilename from "../utilities/splitFilename";
import type { FileViewMode } from "../utilities/fileViewModes";
import DropdownMenu from "./DropdownMenu";
import FileThumbnail from "./FileThumbnail";
import styles from "../FileExplorer.module.scss";

interface FileItemProps {
  file: ScopeFile;
  chatThread: types.ChatThread;
  /** A table row in list view, a tile in gallery view. */
  view: FileViewMode;
  isSelected: boolean;
  /** Keeps every checkbox visible once any file is selected. */
  hasSelection: boolean;
  onToggleSelect: (fileId: string, shiftKey: boolean) => void;
  onDelete: (file: ScopeFile) => void;
  canDelete: boolean;
}

/** One file: a table row in list view, or a tile in gallery view. */
export default function FileItem({
  file,
  chatThread,
  view,
  isSelected,
  hasSelection,
  onToggleSelect,
  onDelete,
  canDelete,
}: FileItemProps) {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  const { downloadFiles } = useDownloadFiles({ chatThread });

  const { baseName, extension } = splitFilename(file.filename);
  const previewTarget = getFilePreviewTarget(file.filename, file.variants);
  const hasOriginal = findVariantPermalink(file.variants, "original") !== null;

  // Plain text when there is nothing to preview.
  const renderPreviewTrigger = (
    className: string,
    content: React.ReactNode
  ) => {
    if (previewTarget === null) {
      return (
        <span className={className} title={file.filename}>
          {content}
        </span>
      );
    }
    return (
      <button
        type="button"
        className={`${className} ${styles.previewButton}`}
        onClick={() => setIsPreviewOpen(true)}
        title={file.filename}
        aria-label={`Preview ${file.filename}`}
      >
        {content}
      </button>
    );
  };

  const checkboxClass = hasSelection
    ? styles.selectCheckbox
    : `${styles.selectCheckbox} ${styles.hoverOnly}`;
  const checkbox = (
    <span
      className={styles.checkboxLabel}
      onClick={(event) => onToggleSelect(file.fileId, event.shiftKey)}
    >
      <input
        type="checkbox"
        className={checkboxClass}
        checked={isSelected}
        aria-label={`Select ${file.filename}`}
        readOnly
      />
    </span>
  );

  const actionsMenu = (
    <DropdownMenu
      buttonLabel={`Actions for ${file.filename}`}
      buttonClassName={styles.menuButton}
      buttonContent={<FiMoreVertical />}
      menuLabel={`Actions for ${file.filename}`}
      items={[
        {
          label: "View",
          onSelect: () => setIsPreviewOpen(true),
          disabled: previewTarget === null,
        },
        { label: "Share", onSelect: () => setIsShareOpen(true) },
        {
          label: "Download",
          onSelect: () => void downloadFiles([file]),
          disabled: !hasOriginal,
        },
        {
          label: "Delete",
          onSelect: () => onDelete(file),
          disabled: !canDelete,
        },
      ]}
    />
  );

  // Outside the clickable elements, because React bubbles clicks in the
  // modals' portals up through this component.
  const modals = (
    <>
      {isPreviewOpen && previewTarget && (
        <FilePreviewModal
          fileName={file.filename}
          chatThread={chatThread}
          permalink={previewTarget.permalink}
          previewKindOverride={previewTarget.previewKindOverride}
          downloadPermalink={previewTarget.downloadPermalink}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
      {isShareOpen && (
        <ShareLinkModal
          fileName={file.filename}
          fileId={file.fileId}
          onClose={() => setIsShareOpen(false)}
        />
      )}
    </>
  );

  if (view === "list") {
    return (
      <tr className={styles.fileRow}>
        <td>{renderPreviewTrigger(styles.baseName, baseName)}</td>
        <td className={styles.extension}>{extension?.toUpperCase()}</td>
        <td>{checkbox}</td>
        <td>
          {actionsMenu}
          {modals}
        </td>
      </tr>
    );
  }

  const tileClass = isSelected
    ? `${styles.tile} ${styles.tileSelected}`
    : styles.tile;
  return (
    <li className={tileClass}>
      {renderPreviewTrigger(
        styles.tilePreview,
        <>
          <span className={styles.thumbnail}>
            <FileThumbnail file={file} chatThread={chatThread} />
            {extension !== null && (
              <span
                className={`${styles.extension} ${styles.thumbnailExtension}`}
              >
                {extension.toUpperCase()}
              </span>
            )}
          </span>
          <span className={`${styles.baseName} ${styles.tileName}`}>
            {baseName}
          </span>
        </>
      )}
      <span className={styles.tileCheckbox}>{checkbox}</span>
      <span className={styles.tileActions}>{actionsMenu}</span>
      {modals}
    </li>
  );
}
